<script setup lang="ts">
interface IProps {
	title?: string;
	loading?: boolean;
	size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'full';
	height?: string;
	animation?: 'fade' | 'slide-in-up' | 'slide-in-right' | 'slide-in-left' | 'scale' | 'slide-mobile' | 'rotate' | 'none';
	showClose?: boolean;
	fullScreen?: boolean;
}

const props = withDefaults(defineProps<IProps>(), {
	size: 'md',
	animation: 'fade',
	showClose: false,
});
const emit = defineEmits<{
	(e: 'toggle'): boolean;
}>();
const isOpen = defineModel<boolean>();

const dialogWidth = computed(() => {
	let width = '';
	switch (props.size) {
		case 'full':
			width = '100%';
			break;
		case 'xxl':
			width = '1400px';
			break;
		case 'xl':
			width = '1200px';
			break;
		case 'lg':
			width = '1000px';
			break;
		case 'md':
			width = '800px';
			break;
		case 'sm':
			width = '600px';
			break;
		case 'xs':
			width = '400px';
	}
	return width;
});

const toggle = ref<boolean>(false);
const handleOutsideClick = () => {
	toggle.value = true;

	setTimeout(() => {
		toggle.value = false;
	}, 250);
};
</script>

<template>
	<Teleport to="#app">
		<transition :name="animation">
			<template v-if="isOpen">
				<div
					class="fixed inset-0 z-[9999999] flex w-full items-center justify-center overflow-y-auto overflow-x-hidden bg-black/35 backdrop-blur-sm md:overflow-hidden"
					:class="{ 'backdrop--enter': isOpen }"
				>
					<span class="absolute w-full h-full" @click="handleOutsideClick"></span>
					<div
						class="z-50 mx-auto items-center overflow-y-auto overflow-x-hidden bg-white shadow-2xl"
						:style="{ width: dialogWidth }"
						:class="[toggle ? 'animate-scale' : '', fullScreen ? 'h-full w-full' : 'h-auto max-h-[calc(100dvh-1rem)] max-w-[95%] rounded-2xl overscroll-contain md:max-h-[80dvh]']"
					>
						<div :style="height">
							<slot name="header">
								<div
									class="sticky top-0 z-[9999999] mx-auto mb-6 flex w-[calc(100%-48px)] items-center border-b border-b-slate-100 bg-white pb-5 pt-6"
									:class="{ '!relative': props.loading }"
								>
									<span v-if="title" class="flex-shrink w-full text-center text-base font-bold text-secondary-60">
										{{ title }}
									</span>
									<div class="flex-grow flex justify-end" v-if="showClose">
										<button type="button" class="rounded-full bg-slate-50 p-2 transition hover:bg-slate-100 hover:drop-shadow" @click="emit('toggle')">
											<svg width="30" height="30" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
												<path
													d="M14.1668 5.83313C14.3431 6.00942 14.3431 6.29788 14.1668 6.47416L10.6411 9.9998L14.1668 13.5254C14.344 13.7026 14.3442 13.9895 14.167 14.1667C13.9898 14.3438 13.702 14.3427 13.5258 14.1665L10.0001 10.6408L6.47448 14.1665C6.29732 14.3436 6.01074 14.3437 5.83366 14.1667C5.65718 13.9902 5.65637 13.7025 5.83346 13.5254L9.3591 9.9998L5.83346 6.47416C5.65629 6.297 5.65649 6.0105 5.83366 5.83333C6.01082 5.65617 6.2974 5.65605 6.47448 5.83313L10.0001 9.35877L13.5258 5.83313C13.702 5.65685 13.9905 5.65685 14.1668 5.83313Z"
													fill="#2A3C55"
												/>
											</svg>
										</button>
									</div>
									<div class="progress-animation" v-if="props.loading"></div>
								</div>
							</slot>
							<div class="mb-6 mt-4 px-6 md:mt-auto">
								<slot name="body" />
							</div>
							<!-- why do we use z-index 999999? Because the neshan uses z-index 999999 -->
							<div class="sticky bottom-0 z-[999999] bg-white py-3">
								<slot name="footer" />
							</div>
						</div>
					</div>
				</div>
			</template>
		</transition>
	</Teleport>
</template>

<style scoped lang="scss">
/* Animations */
.fade-enter-active,
.fade-leave-active {
	transition: all 0.2s;
	opacity: 1;
}
.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}

/* Slide-in-up animation*/
.slide-in-up-enter-active,
.slide-in-up-leave-active {
	transition: all 0.2s;
	transform: translateY(0);
}
.slide-in-up-enter,
.slide-in-up-leave-to {
	opacity: 0;
	transform: translateY(20px);
}

/* Slide-in-right animation*/
.slide-in-right-enter-active,
.slide-in-right-leave-active {
	transition: all 0.2s;
	transform: translateX(0);
}
.slide-in-right-enter,
.slide-in-right-leave-to {
	opacity: 0;
	transform: translateX(20px);
}

/* Slide-in-left animation*/
.slide-in-left-enter-active,
.slide-in-left-leave-active {
	transition: all 0.2s;
	transform: translateX(0);
}
.slide-in-left-enter,
.slide-in-left-leave-to {
	opacity: 0;
	transform: translateX(-20px);
}

/* Scale animation*/
.scale-enter-active,
.scale-leave-active {
	transition: all 0.2s;
	transform: scale(1);
}
.scale-enter-from,
.scale-leave-to {
	opacity: 0;
	transform: scale(0);
}

.animate-scale {
	animation: scale 250ms cubic-bezier(0.68, -0.6, 0.32, 1.6);
	transform: scale(1);
}

@keyframes scale {
	0%,
	100% {
		transform: scale(100%);
	}
	50% {
		transform: scale(102%);
	}
}

/* Rotate animation*/
.rotate-enter-active,
.rotate-leave-active {
	transition: all 0.2s;
	transform: scale(1) rotate(-360deg);
}
.rotate-enter-from,
.rotate-leave-to {
	opacity: 0;
	transform: scale(0) rotate(360deg);
}

.bounce-enter-active {
	animation: bounce-in 0.5s;
}
.bounce-leave-active {
	animation: bounce-in 0.5s reverse;
}

.slide-mobile-enter-active,
.slide-mobile-leave-active {
	transition: all 0.2s;
	transform: translateY(0);
}
.slide-mobile-enter-from,
.slide-mobile-leave-to {
	opacity: 0;
	transform: translateY(20px);
}

.slide-mobile-request-enter-active,
.slide-mobile-request-leave-active {
	transition: all 0.2s;
	transform: translateY(0);
}
.slide-mobile-request-enter-from,
.slide-mobile-request-leave-to {
	opacity: 0;
	transform: translateY(20px);
}

@keyframes bounce-in {
	0% {
		transform: scale(0);
	}
	50% {
		transform: scale(1.25);
	}
	100% {
		transform: scale(1);
	}
}

@media screen and (max-width: 1024px) {
	.slide-mobile-request {
		width: 100%;
		min-height: auto;
		max-height: 100%;
		overflow-y: auto;
		background: white;
		border-radius: 1rem;
		overflow-x: hidden;
		position: fixed;
		bottom: 0;
	}
}
@media screen and (max-width: 640px) {
	.slide-mobile {
		width: 100% !important;
		min-height: auto;
		max-height: 100%;
		bottom: 0;
		right: 0;
		position: absolute;
		background: white;
		overflow-x: hidden;
	}
}
</style>
